import { RichTextEditorProvider } from 'components/Editor/RichTextEditor/RichTextEditorProvider';
import useAuthContext from 'hooks/useAuthContext';
import { useDialogs } from '@toolpad/core/useDialogs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    getGeneralStudentAgreementOptions,
    updateGeneralStudentAgreementMutation,
} from '@drivingdoc/api-client/generated/react-query';
import AgreementForm, { AgreementOutputValues } from 'components/AgreementForm';
import FeedbackDialog from 'components/Dialogs/FeedbackDialog';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';
import PageContainer from 'components/PageContainer';
import TermsTheme from 'components/Editor/themes/TermsTheme';
import { AgreementResult } from 'types/agreements';


export function TermsOfService() {
    const { tenantId } = useAuthContext();
    const dialogs = useDialogs();
    const queryClient = useQueryClient();
    const updateGeneralStudentAgreement = useMutation(updateGeneralStudentAgreementMutation());

    // fetch the agreement
    const agreement = useQuery({
        ...getGeneralStudentAgreementOptions({
            path: {
                tenantId: tenantId ?? '',
            },
        }),
        enabled: !!tenantId,
    });
    const agreementData = agreement.data as AgreementResult | undefined;

    const onSubmit = async (data: AgreementOutputValues) => {
        try {
            // show confirmation dialog
            const confirmed = await dialogs.confirm(`Update General Terms of Service?`, {
                okText: 'Update',
                cancelText: 'Cancel',
            });

            // if user cancels, return
            if (!confirmed) {
                return;
            }

            const result = await updateGeneralStudentAgreement.mutateAsync({
                path: {
                    tenantId: tenantId ?? '',
                },
                body: {
                    content: data.content as unknown as Record<string, unknown>
                },
            });

            if (result) {
                // close the dialog
                // handleClose();

                // invalidate the query key
                queryClient.invalidateQueries({
                    queryKey: ['general_student_agreement'],
                });

                // show success message
                dialogs.open(FeedbackDialog, {
                    icon: 'success',
                    title: 'General Terms of Service Updated',
                });
            }
        } catch (error) {
            dialogs.open(FeedbackDialog, {
                title: 'Error',
                message: getApiErrorMessage(error),
            });
        }
    };


    return (
        <PageContainer title="General Terms of Service">
            <RichTextEditorProvider namespace='general-terms-of-service' theme={TermsTheme}>
                <AgreementForm
                    initialData={{
                        content: agreementData?.agreement_versions?.[0].content
                    }}
                    isLoading={agreement.isLoading}
                    onSubmit={onSubmit}
                />
            </RichTextEditorProvider>
        </PageContainer>
    );
}

export default TermsOfService;